import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useI18n } from "@/context";
import {
  hasPartnerPortalAccess,
  isPartnerMasterRole,
  PARTNER_TEAM_ROLE_NAME,
  useAuth,
} from "@/context/auth";

import { ITeamFormProps } from "./team.hooks.interface";
import {
  createUser,
  fetchRoles,
  fetchUserPermissions,
  fetchZipCode,
  getUserById,
  updateUser,
} from "./team-form.api";
import {
  teamFormSchema,
  teamFormUpdateSchema,
  TUserTeamFormData,
} from "./team-form.schema";
import { DocumentType, TMappedRole, TUserCreate } from "./team-form.types";

export const useTeam = (): ITeamFormProps => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [isProductFormOpen, setIsProductFormOpen] = React.useState(false);
  const openProductForm = React.useCallback(
    () => setIsProductFormOpen(true),
    []
  );
  const [tab, setTab] = useState<"data" | "login" | "permission">("data");
  const [allRoles, setAllRoles] = React.useState<TMappedRole[]>([]);
  const [prefilledUserId, setPrefilledUserId] = React.useState<string | null>(
    null
  );
  const [currentUser, setCurrentUser] = React.useState<any>(null);

  const userIdFromQuery = React.useMemo(
    () => new URLSearchParams(window.location.search).get("id"),
    []
  );

  const form = useForm<TUserTeamFormData>({
    resolver: zodResolver(
      userIdFromQuery ? teamFormUpdateSchema : teamFormSchema
    ),
    defaultValues: {
      data: {
        firstName: "",
        lastName: "",
        optionCpfCnpj: "cpf",
        document: "",
        birthDate: new Date(),
        phone: "",
        zipcode: "",
        state: "",
        city: "",
        neighborhood: "",
        street: "",
        number: "",
        complement: "",
      },
      login: {
        email: "",
        password: "",
      },
      permissions: {
        accessLevel: "",
        selectedPermissions: [],
      },
    },
  });

  const loadRoles = React.useCallback(async () => {
    try {
      const rolesData = await fetchRoles();

      const mappedRoles = rolesData.map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description,
        permissions: role.permissions,
      }));

      setAllRoles(mappedRoles);
    } catch (error) {
      console.error("Erro ao carregar roles:", error);
    }
  }, []);

  const uniquePermissions = React.useMemo(() => {
    return Array.from(
      allRoles
        .flatMap((role) => role.permissions)
        .reduce((map, permission) => {
          if (!map.has(permission.id)) {
            map.set(permission.id, permission);
          }
          return map;
        }, new Map())
        .values()
    );
  }, [allRoles]);

  const isPartnerPortalUser = hasPartnerPortalAccess(auth.roles);
  const partnerCreatesTeamOnly = isPartnerMasterRole(auth.roles);

  const { data: actorPermissionsData } = useQuery({
    queryKey: ["user-effective-permissions", auth.id],
    queryFn: () => fetchUserPermissions(auth.id),
    enabled: Boolean(auth.id && isPartnerPortalUser),
  });

  const actorAllowedPermissionIds = React.useMemo(() => {
    if (!isPartnerPortalUser || !actorPermissionsData?.permissions) {
      return null;
    }
    return new Set(actorPermissionsData.permissions.map((p) => p.id));
  }, [actorPermissionsData?.permissions, isPartnerPortalUser]);

  const visibleRoles = React.useMemo(() => {
    if (!partnerCreatesTeamOnly) {
      return allRoles;
    }
    return allRoles.filter((r) => r.name === PARTNER_TEAM_ROLE_NAME);
  }, [allRoles, partnerCreatesTeamOnly]);

  const scopedUniquePermissions = React.useMemo(() => {
    if (!actorAllowedPermissionIds) {
      return uniquePermissions;
    }
    return uniquePermissions.filter((p) =>
      actorAllowedPermissionIds.has(p.id)
    );
  }, [uniquePermissions, actorAllowedPermissionIds]);

  React.useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  React.useEffect(() => {
    const isPartnerTeamOnly =
      isPartnerPortalUser && !partnerCreatesTeamOnly;
    if (isPartnerTeamOnly && !userIdFromQuery) {
      navigate("/users", { replace: true });
    }
  }, [isPartnerPortalUser, partnerCreatesTeamOnly, userIdFromQuery, navigate]);

  React.useEffect(() => {
    const userId = userIdFromQuery;
    if (!userId) return;
    if (prefilledUserId === userId) return;
    (async () => {
      try {
        const resp: any = await getUserById(userId);
        const user: any = Array.isArray(resp) ? resp[0] : resp || {};
        setCurrentUser(user);
        // z.coerce.date exige Date aqui
        const birthDate = user.birth_date
          ? new Date(user.birth_date)
          : new Date();
        const street = user.address_street ?? user.address?.street ?? "";
        const number = user.address_number ?? user.address?.number ?? "";
        const complement =
          user.address_complement ?? user.address?.complement ?? "";
        const neighborhood =
          user.address_neighborhood ?? user.address?.neighborhood ?? "";
        const city = user.address_city ?? user.address?.city ?? "";
        const state = user.address_state ?? user.address?.state ?? "";
        const postal_code =
          user.address_postal_code ?? user.address?.postal_code ?? "";

        // Role from backend can come as string, object or array
        const backendRoleName = Array.isArray(user.roles)
          ? user.roles[0]?.name
          : typeof user.role === "object"
          ? user.role?.name
          : user.role;
        const roleName =
          allRoles.find(
            (r) => r.name === backendRoleName || r.id === (user.role || "")
          )?.name ||
          backendRoleName ||
          "";

        // Permissions from backend: can be array of strings (ids or names) or array of objects { id, name }
        let selectedPermissionNames: string[] = [];
        if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          const first = user.permissions[0] as any;
          if (typeof first === "object") {
            selectedPermissionNames = (user.permissions as any[])
              .map((p) => p?.name)
              .filter(Boolean);
          } else if (typeof first === "string") {
            const set = new Set(user.permissions as string[]);
            selectedPermissionNames = uniquePermissions
              .filter((p) => set.has(p.id) || set.has(p.name))
              .map((p) => p.name);
          }
        } else if (roleName) {
          const role = allRoles.find((r) => r.name === roleName);
          selectedPermissionNames = (role?.permissions || []).map(
            (p) => p.name
          );
        }

        form.reset({
          data: {
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            optionCpfCnpj: (user.document_type as any) || "cpf",
            document: user.document || "",
            birthDate,
            phone: user.phone || "",
            zipcode: postal_code,
            state,
            city,
            neighborhood,
            street,
            number,
            complement,
          },
          login: {
            email: user.email || "",
            password: "",
          },
          permissions: {
            accessLevel: roleName,
            selectedPermissions: selectedPermissionNames,
          },
        });
        // Garantir que os checkboxes reflitam imediatamente as permissões carregadas
        form.setValue(
          "permissions.selectedPermissions",
          selectedPermissionNames,
          {
            shouldDirty: false,
            shouldValidate: true,
          }
        );
        setPrefilledUserId(userId);
      } catch (e) {
        console.error("Erro ao carregar usuário:", e);
      }
    })();
  }, [allRoles, uniquePermissions, userIdFromQuery, prefilledUserId]);

  const handleAddressBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const zipcode = event.target.value;
    let addressData = null;

    try {
      addressData = await fetchZipCode(zipcode);
    } catch (error) {
      console.error("Error fetching address:", error);
    }

    form.clearErrors("data.zipcode");

    if (!zipcode) return;

    if (addressData) {
      form.setValue("data.state", addressData.state_acronym);
      form.setValue("data.city", addressData.city_name);
      form.setValue("data.neighborhood", addressData.neighborhood_name);
      form.setValue("data.street", addressData.street_full);
    } else {
      form.setError("data.zipcode", {
        type: "custom",
        message: t("errors.zipcodeNotFound"),
      });
    }
  };
  const onSubmit = React.useCallback(
    async (values: TUserTeamFormData) => {
      const role = allRoles.find(
        (r) => r.name === values.permissions.accessLevel
      );
      const permissionIds = scopedUniquePermissions
        .filter((p) => values.permissions.selectedPermissions.includes(p.name))
        .map((p) => p.id);

      const payload: TUserCreate = {
        first_name: values.data.firstName,
        last_name: values.data.lastName,
        email: values.login.email,
        password: values.login.password,
        document: values.data.document.replace(/[^0-9]/g, ""),
        document_type: values.data.optionCpfCnpj as DocumentType,
        birth_date: values.data.birthDate
          ? new Date(values.data.birthDate).toISOString()
          : undefined,
        phone: values.data.phone.replace(/[^0-9]/g, ""),
        address: {
          street: values.data.street,
          number: values.data.number,
          complement: values.data.complement ?? "",
          neighborhood: values.data.neighborhood,
          city: values.data.city,
          state: values.data.state,
          country: "Brasil",
          postal_code: values.data.zipcode.replace(/[^0-9]/g, ""),
        },
        role: role?.id ?? "",
        permissions: permissionIds,
      };

      try {
        const searchParams = new URLSearchParams(window.location.search);
        const userId = searchParams.get("id");
        if (userId) {
          const payloadToUpdate: any = { ...payload };
          if (!values.login.password || values.login.password.trim() === "") {
            delete payloadToUpdate.password;
          }
          await updateUser(userId, payloadToUpdate as TUserCreate);
        } else {
          await createUser(payload);
        }
        navigate("/users");
      } catch (err) {
        console.error("Erro ao salvar usuário:", err);
      }
    },
    [allRoles, scopedUniquePermissions, navigate]
  );
  return {
    openProductForm,
    isProductFormOpen,
    open: isProductFormOpen,
    form,
    setTab,
    tab,
    handleAddressBlur,
    roles: visibleRoles,
    loadRoles,
    uniquePermissions: scopedUniquePermissions,
    onSubmit: (values: TUserTeamFormData) => onSubmit(values),
    currentUser,
    userIdFromQuery,
  };
};
